import type {
    AccountCharacter,
    AccountCharacterCreateRequest,
    CharacterDisciplines,
    Equipment,
} from '@sorrem/shared';
import { useCallback, useEffect, useMemo } from 'react';
import type {
    CreateCharacterResult,
    CurrentPlayerCharacterState,
    UIPartyCharacter,
    UIInventoryItem,
} from '@sorrem-client/types';
import { DEFAULT_CHARACTER_MODEL_ID, listCharacterVisualItemIds, resolveWieldedItemTiers, getStoredLoadout, resolveWearerDisciplines, resolvePlayerPower } from '@sorrem/shared';

import {
    resolveApiErrorMessage,
    useCreateCharacterMutation,
    useDeleteCharacterMutation,
    useGetAccountQuery,
    useGetCharactersQuery,
    useGetCharacterSkillsQuery,
    usePatchSkillMutation,
    useReorderCharactersMutation,
} from '@sorrem-client/ui/store';
import { toUIInventoryItem } from '@sorrem-client/ui/shared/inventory-item';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { findCurrentCharacter } from './use-current-character-id';
import { useSelectedCharacterId } from './use-selected-character-id';

/** Disciplines for a wearer the rules never weaken; a character always has its own. */
const NO_DISCIPLINES: CharacterDisciplines = { veilAttunement: 0, martialDiscipline: 0 };

const NOT_SIGNED_IN = 'You need to be signed in to create a character.';

function toPartyCharacter(character: AccountCharacter): UIPartyCharacter {
    const displayName = resolveDisplayName(character.name);
    // Each carried or stored item's card says whether the character falls short of its requirement (#250),
    // and the skill tree and mastery dials show the same numbers (#330).
    const disciplines = resolveWearerDisciplines(character) ?? NO_DISCIPLINES;
    const carriedItems = toInventorySlots(character.carriedItems, disciplines);
    const storedItems = toInventorySlots(character.storedItems, disciplines);
    const { maxCarriedSlots, maxStoredSlots } = character;
    // `characterModel` is the creation-time selection and, as returned by the API, is an outfit map
    // with no `baseModelId`; `characterModelId` is the field that names the model to render.
    const characterModelId = character.characterModelId || DEFAULT_CHARACTER_MODEL_ID;

    return {
        id: character.id,
        name: displayName,
        characterModelId,
        modelAttachmentItemIds: listCharacterVisualItemIds(getStoredLoadout(character), character.outfit),
        modelOutfitModifiers: character.outfitModifiers,
        modelItemTiers: resolveWieldedItemTiers(getStoredLoadout(character)),
        readiness: resolvePlayerPower(character).readiness,
        hp: character.essence ?? character.maxEssence ?? 100,
        maxHp: character.maxEssence ?? 100,
        essence: character.essence ?? character.maxEssence ?? 100,
        maxEssence: character.maxEssence ?? 100,
        status: character.status,
        attributes: character.attributes,
        coreStats: character.coreStats,
        skills: character.skills,
        equipped: getStoredLoadout(character),
        carriedItems,
        storedItems,
        maxCarriedSlots,
        maxStoredSlots,
        permanentEssence: character.permanentEssence,
        attributeRespecCount: character.attributeRespecCount ?? 0,
        veilAttunementSpent: character.veilAttunementSpent ?? 0,
        disciplines,
        skillRespecCount: character.skillRespecCount ?? 0,
        recentRunPraxis: character.recentRunPraxis ?? [],
    };
}

function toInventorySlots(items: Array<Equipment | null>, disciplines: CharacterDisciplines | undefined): Array<UIInventoryItem | undefined> {
    return items.map((item) => {
        if (item === null) {
            return undefined;
        }

        return toUIInventoryItem(item, disciplines);
    });
}

function resolveDisplayName(serverName: string): string {
    const trimmedServerName = serverName.trim();
    return trimmedServerName.length > 0 ? trimmedServerName : '';
}

export function useCurrentPlayerCharacter(): CurrentPlayerCharacterState {
    const accountId = getStoredCanonicalAccountId();
    const skip = accountId === null;

    const {
        selectedCharacterId,
        setSelectedCharacterId,
    } = useSelectedCharacterId();

    const {
        data: accountProfile,
        isFetching: isAccountFetching,
        isError: isAccountError,
    } = useGetAccountQuery(accountId ?? '', { skip });

    const {
        data: characters,
        isFetching: isCharactersFetching,
        isError: isCharactersError,
        refetch: refetchCharacters,
    } = useGetCharactersQuery(accountId ?? '', { skip });

    const selectedCharacter = useMemo(() => findCurrentCharacter(characters, selectedCharacterId), [characters, selectedCharacterId]);

    const {
        data: skills,
        isFetching: isSkillsFetching,
        refetch: refetchSkills,
    } = useGetCharacterSkillsQuery(
        { accountId: accountId ?? '', characterId: selectedCharacter?.id ?? '' },
        { skip: skip || selectedCharacter === undefined },
    );

    const [triggerPatchSkill, { isLoading: isUpdatingSkills }] = usePatchSkillMutation();
    const [triggerCreateCharacter, { isLoading: isCreatingCharacter }] = useCreateCharacterMutation();
    const [triggerDeleteCharacter, { isLoading: isDeletingCharacter, error: deleteCharacterError }] = useDeleteCharacterMutation();
    const [triggerReorderCharacters, { isLoading: isReorderingCharacters, error: reorderCharactersError }] = useReorderCharactersMutation();

    useEffect(() => {
        if (characters === undefined) {
            return;
        }

        if (characters.length === 0) {
            setSelectedCharacterId(null);
            return;
        }

        const hasSelectedCharacter = selectedCharacterId !== null
            && characters.some((character) => character.id === selectedCharacterId);
        if (!hasSelectedCharacter) {
            setSelectedCharacterId(characters[0]?.id ?? null);
        }
    }, [characters, selectedCharacterId]);

    const partyCharacters = useMemo<UIPartyCharacter[]>(() => {
        if (characters === undefined || accountProfile === undefined) {
            return [];
        }

        return characters.map((character) => toPartyCharacter(
            character.id === selectedCharacter?.id
                ? { ...character, skills: skills ?? character.skills }
                : character,
        ));
    }, [accountProfile, characters, selectedCharacter?.id, skills]);

    const character = useMemo<UIPartyCharacter | null>(() => {
        if (selectedCharacter === undefined || accountProfile === undefined) {
            return null;
        }
        return partyCharacters.find((entry) => entry.id === selectedCharacter.id) ?? null;
    }, [accountProfile, partyCharacters, selectedCharacter]);

    const refreshCharacter = useCallback(async (): Promise<void> => {
        await Promise.all([refetchCharacters(), refetchSkills()]);
    }, [refetchCharacters, refetchSkills]);

    const selectCharacter = useCallback(async (characterId: string): Promise<boolean> => {
        const normalizedId = characterId.trim();
        if (normalizedId.length === 0) {
            return false;
        }

        setSelectedCharacterId(normalizedId);
        return true;
    }, [setSelectedCharacterId]);

    const createCharacter = useCallback(async (body: AccountCharacterCreateRequest): Promise<CreateCharacterResult> => {
        if (!accountId || !accountProfile) {
            return { error: NOT_SIGNED_IN };
        }

        try {
            const createdCharacter = await triggerCreateCharacter({ accountId, body }).unwrap();
            await refetchCharacters();
            setSelectedCharacterId(createdCharacter.id);
            return { character: toPartyCharacter(createdCharacter) };
        } catch (failure) {
            // The server's own reason (the character limit, an allocation it refuses), for the creation panel (#330).
            return { error: resolveApiErrorMessage(failure, 'Unable to create character right now. Please try again.') };
        }
    }, [accountId, accountProfile, refetchCharacters, triggerCreateCharacter]);

    const reorderCharacters = useCallback(async (orderedCharacterIds: string[]): Promise<boolean> => {
        if (!accountId) {
            return false;
        }

        try {
            await triggerReorderCharacters({
                accountId,
                body: {
                    orderedCharacterIds,
                },
            }).unwrap();
            return true;
        } catch {
            return false;
        }
    }, [accountId, triggerReorderCharacters]);

    const deleteCharacter = useCallback(async (characterId: string): Promise<boolean> => {
        if (!accountId) {
            return false;
        }

        try {
            await triggerDeleteCharacter({ accountId, characterId }).unwrap();
            if (selectedCharacterId === characterId) {
                setSelectedCharacterId(null);
            }
            await refetchCharacters();
            return true;
        } catch {
            return false;
        }
    }, [accountId, refetchCharacters, selectedCharacterId, setSelectedCharacterId, triggerDeleteCharacter]);

    const setSkillPinned = useCallback(async (skillId: string, pinned: boolean): Promise<void> => {
        const characterId = selectedCharacter?.id;
        if (!accountId || !characterId) {
            return;
        }
        await triggerPatchSkill({ accountId, characterId, skillId, body: { isPinned: pinned } });
    }, [accountId, selectedCharacter?.id, triggerPatchSkill]);

    if (skip) {
        return {
            accountProfile: null,
            characters: [],
            character: null,
            selectedCharacterId: null,
            isLoading: false,
            isUpdatingSkills: false,
            isCreatingCharacter: false,
            isDeletingCharacter: false,
            isReorderingCharacters: false,
            error: 'No active account session found.',
            refreshCharacter: async () => undefined,
            selectCharacter: async () => false,
            createCharacter: async (): Promise<CreateCharacterResult> => ({ error: NOT_SIGNED_IN }),
            deleteCharacter: async () => false,
            reorderCharacters: async () => false,
            setSkillPinned: async () => undefined,
        };
    }

    const isLoading = (isAccountFetching || isCharactersFetching || isSkillsFetching) && character === null;
    const hasError = isAccountError || isCharactersError;
    // A failed create says why on the creation panel itself (`createCharacter`'s result), not here as well.
    const hasMutationError = deleteCharacterError !== undefined
        || reorderCharactersError !== undefined;

    return {
        accountProfile: accountProfile ?? null,
        characters: partyCharacters,
        character,
        selectedCharacterId,
        isLoading,
        isUpdatingSkills,
        isCreatingCharacter,
        isDeletingCharacter,
        isReorderingCharacters,
        error: hasError
            ? 'Failed to load character data from the server.'
            : hasMutationError
                ? 'Unable to update your character roster right now.'
                : null,
        refreshCharacter,
        selectCharacter,
        createCharacter,
        deleteCharacter,
        reorderCharacters,
        setSkillPinned,
    };
}
